/***
 *
 *   STEP CARD
 *   One step in a stage column, and the blue start card in the Starts column.
 *
 *   Normally the whole card is a button that opens the step drawer. In Expand
 *   all the card shows its detail inline (body, detail rows with labels above
 *   values, Settings link) and is not interactive itself, so the counts and
 *   links inside it stay reachable without nesting controls in a button.
 *
 *   Steps that are switched off ("available") or not part of the flow
 *   ("not_included") are dashed and muted; only "available" gets the pill.
 *
 *   PROPS
 *   step: { id, title, sub?, body, state, details[], settings? }
 *   start: { title, sub, details[], public_link?, settings? }
 *   expanded: show detail inline instead of opening the drawer
 *   onOpen(element): open the drawer for this card
 *
 **********/

import React from "react";
import { DetailList, SettingsLink, cx } from "./details";
import styles from "./workflow.module.scss";

const isOff = (step) => step?.state === "available" || step?.state === "not_included";

/* Enter opens it like a click; preventDefault stops the button's own click
   following, so the drawer opens once. Space is left to the button. */
function onEnter(open) {
  return (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    open(e.currentTarget);
  };
}

export function StepCard({ step, expanded, onOpen }) {
  const off = isOff(step);
  const head = (
    <>
      <span className={styles.stepTitle}>{step.title}</span>
      {step.sub && <span className={styles.stepSub}>{step.sub}</span>}
      {step.state === "available" && <span className={styles.pill}>Available to switch on</span>}
    </>
  );

  if (expanded) {
    return (
      <div className={cx(styles.step, styles.stepStatic, off && styles.off)}>
        {head}
        <div className={styles.det}>
          {step.body && <p>{step.body}</p>}
          <DetailList rows={step.details} />
          {step.settings?.path && (
            <div className={styles.detLink}>
              <SettingsLink settings={step.settings} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={cx(styles.step, off && styles.off)}
      aria-haspopup="dialog"
      onClick={(e) => onOpen(e.currentTarget)}
      onKeyDown={onEnter(onOpen)}
    >
      {head}
    </button>
  );
}

export function StartCard({ start, expanded, onOpen }) {
  const head = (
    <>
      <span className={styles.startTitle}>{start.title}</span>
      {start.sub && <span className={styles.stepSub}>{start.sub}</span>}
    </>
  );

  if (expanded) {
    return (
      <div className={cx(styles.step, styles.stepStatic, styles.start)}>
        {head}
        {Array.isArray(start.details) && start.details.length > 0 && (
          <div className={styles.det}>
            <DetailList rows={start.details} />
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={cx(styles.step, styles.start)}
      aria-haspopup="dialog"
      onClick={(e) => onOpen(e.currentTarget)}
      onKeyDown={onEnter(onOpen)}
    >
      {head}
    </button>
  );
}
